import React from "react";
import { Link, useParams } from "react-router-dom";
import {
  FaArrowLeft,
  FaBalanceScale,
  FaCalendarAlt,
  FaCheckCircle,
  FaClock,
  FaExclamationTriangle,
  FaHistory,
  FaIdBadge,
  FaInfoCircle,
  FaMinusCircle,
  FaUndo,
  FaUserGraduate,
  FaUserTie,
} from "react-icons/fa";
import { Spinner } from "src/components/shared/atoms/spinner";
import { useTheme } from "src/context/theme.context";
import {
  useGetUserByIdQuery,
  useGetUserMeetingPolicyEventsQuery,
  useGetUserMeetingPolicyStatsQuery,
  useResetMeetingPolicyStatsMutation,
} from "../../services/user.Service";
import Modal from "src/components/modal/modal";
import { showToast } from "src/store/toast.store";
import { useAppDispatch } from "src/store";
import {
  MeetingPolicyEvent,
  MeetingPolicyEventType,
  MeetingPolicyRestriction,
  MeetingPolicyRole,
  UserMeetingPolicyStats,
} from "../../types/meeting-policy.entity";

const roles: MeetingPolicyRole[] = ["LEARNER", "COACH"];

const getPolicyRolesForUser = (userRoles?: string[]): MeetingPolicyRole[] => {
  const normalizedRoles = (userRoles || []).map((role) => role.toLowerCase());
  const hasLearnerRole = normalizedRoles.some((role) => role.includes("learner"));
  const hasCoachRole = normalizedRoles.some((role) => role.includes("coach"));
  const policyRoles: MeetingPolicyRole[] = [];

  if (hasLearnerRole) policyRoles.push("LEARNER");
  if (hasCoachRole) policyRoles.push("COACH");

  return policyRoles.length > 0 ? policyRoles : roles;
};

const toDate = (value: unknown): Date | null => {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value === "object" && value !== null && "toDate" in value && typeof (value as { toDate: () => Date }).toDate === "function") {
    return (value as { toDate: () => Date }).toDate();
  }
  if (typeof value === "object" && value !== null && "seconds" in value) {
    return new Date(Number((value as { seconds: number }).seconds) * 1000);
  }

  const parsed = new Date(String(value));
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const formatDate = (value: unknown) => {
  const date = toDate(value);
  if (!date) return "Not recorded";

  return date.toLocaleString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatLabel = (value?: string) => {
  if (!value) return "Not recorded";
  return value
    .toLowerCase()
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
};

const getQueryErrorText = (error: unknown) => {
  if (!error) return "Unknown error";
  const anyError = error as { data?: unknown; message?: unknown; error?: unknown };

  if (typeof error === "string") return error;
  if (anyError.data) return typeof anyError.data === "string" ? anyError.data : JSON.stringify(anyError.data);
  if (anyError.message) return String(anyError.message);
  if (anyError.error) return String(anyError.error);

  try {
    return JSON.stringify(error);
  } catch {
    return String(error);
  }
};

const getFirstLink = (text: string) => text.match(/https?:\/\/[^\s)]+/)?.[0];

const formatReliability = (rating?: number) => {
  if (typeof rating !== "number") return "N/A";
  return `${normalizeReliability(rating).toFixed(1)}%`;
};

type PolicyTone = "emerald" | "amber" | "rose" | "blue";

const normalizeReliability = (rating?: number) => {
  if (typeof rating !== "number") return 0;
  return Math.max(0, Math.min(100, rating <= 1 ? rating * 100 : rating));
};

const getReliabilityTone = (rating?: number): PolicyTone => {
  if (typeof rating !== "number") return "blue";
  const normalizedRating = normalizeReliability(rating);
  if (normalizedRating >= 90) return "emerald";
  if (normalizedRating >= 75) return "amber";
  return "rose";
};

const getEventTone = (eventType: MeetingPolicyEventType, pointsDelta: number): PolicyTone => {
  if (eventType === "COMPLETED") return "emerald";
  if (eventType === "ADMIN_RESET") return "blue";
  if (eventType === "LATE_ARRIVAL" && pointsDelta === 0) return "amber";
  return pointsDelta < 0 ? "rose" : "amber";
};

const getToneClasses = (tone: PolicyTone, isDarkMode: boolean) => {
  const darkClasses = {
    emerald: "border-emerald-700/60 bg-emerald-950/35 text-emerald-100",
    amber: "border-amber-700/60 bg-amber-950/35 text-amber-100",
    rose: "border-rose-700/60 bg-rose-950/35 text-rose-100",
    blue: "border-sky-700/60 bg-sky-950/35 text-sky-100",
  };
  const lightClasses = {
    emerald: "border-emerald-200 bg-emerald-50/85 text-emerald-900",
    amber: "border-amber-200 bg-amber-50/85 text-amber-900",
    rose: "border-rose-200 bg-rose-50/85 text-rose-900",
    blue: "border-sky-200 bg-sky-50/85 text-sky-900",
  };

  return isDarkMode ? darkClasses[tone] : lightClasses[tone];
};

const getToneStrokeClass = (tone: PolicyTone) => {
  const strokeClasses = {
    emerald: "stroke-emerald-500",
    amber: "stroke-amber-500",
    rose: "stroke-rose-500",
    blue: "stroke-cyan-500",
  };

  return strokeClasses[tone];
};

const getToneTextClass = (tone: PolicyTone) => {
  const textClasses = {
    emerald: "text-emerald-600",
    amber: "text-amber-600",
    rose: "text-rose-600",
    blue: "text-sky-600",
  };

  return textClasses[tone];
};

const MetadataSummary = ({ metadata, isDarkMode }: { metadata?: Record<string, unknown>; isDarkMode: boolean }) => {
  if (!metadata || Object.keys(metadata).length === 0) return <span className="text-slate-400">None</span>;

  const priorityKeys = ["adminId", "reason", "resetAll"];
  const primitiveEntries = Object.entries(metadata).filter(
    ([, value]) => value === null || ["string", "number", "boolean"].includes(typeof value)
  );
  const selectedEntries = [
    ...priorityKeys
      .filter((key) => Object.prototype.hasOwnProperty.call(metadata, key))
      .map((key) => [key, metadata[key]] as [string, unknown]),
    ...primitiveEntries.filter(([key]) => !priorityKeys.includes(key)),
  ].slice(0, 4);

  if (selectedEntries.length === 0) {
    return <span className="text-slate-400">Metadata recorded</span>;
  }

  return (
    <div className="flex flex-wrap gap-1.5">
      {selectedEntries.map(([key, value]) => (
        <span
          key={key}
          className={`rounded-lg px-2 py-1 text-[11px] font-medium ${
            isDarkMode ? "bg-slate-900 text-slate-300 ring-1 ring-slate-800" : "bg-white text-slate-700 ring-1 ring-slate-200"
          }`}
        >
          {formatLabel(key)}: {String(value)}
        </span>
      ))}
    </div>
  );
};

const RestrictionBadge = ({
  restriction,
  isDarkMode,
}: {
  restriction?: MeetingPolicyRestriction;
  isDarkMode: boolean;
}) => {
  if (!restriction) {
    return (
      <div className={`rounded-2xl border px-4 py-3 text-sm shadow-sm ${getToneClasses("emerald", isDarkMode)}`}>
        <div className="flex items-center gap-2 font-bold">
          <FaCheckCircle />
          No active restriction
        </div>
      </div>
    );
  }

  const tone = restriction.level === "HARD" ? "rose" : "amber";

  return (
    <div className={`rounded-2xl border px-4 py-3 text-sm shadow-sm ${getToneClasses(tone, isDarkMode)}`}>
      <div className="flex items-center gap-2 font-extrabold">
        <FaExclamationTriangle />
        {restriction.level} restriction active
      </div>
      <div className="mt-2 leading-6">
        Expires {formatDate(restriction.expiresAt)}
        <div className="break-all text-xs opacity-80">Reason event: {restriction.reasonEventId}</div>
      </div>
    </div>
  );
};

const CounterTile = ({
  label,
  value,
  tone,
  isDarkMode,
}: {
  label: string;
  value?: number;
  tone: PolicyTone;
  isDarkMode: boolean;
}) => (
  <div className={`rounded-2xl border px-4 py-3 shadow-sm transition-colors ${getToneClasses(tone, isDarkMode)}`}>
    <div className="text-[11px] font-bold uppercase tracking-[0.1em] opacity-75">{label}</div>
    <div className="mt-1 text-2xl font-black leading-none tabular-nums">{value ?? 0}</div>
  </div>
);

const ReliabilityProgress = ({
  rating,
  tone,
  isDarkMode,
}: {
  rating?: number;
  tone: PolicyTone;
  isDarkMode: boolean;
}) => (
  <div className="relative flex h-36 w-36 shrink-0 items-center justify-center">
    <svg className="h-full w-full -rotate-90" viewBox="0 0 120 120" role="img" aria-label={`Reliability ${formatReliability(rating)}`}>
      <circle
        cx="60"
        cy="60"
        r="48"
        fill="none"
        strokeWidth="10"
        className={isDarkMode ? "stroke-slate-800" : "stroke-slate-200"}
      />
      <circle
        cx="60"
        cy="60"
        r="48"
        fill="none"
        strokeWidth="10"
        strokeLinecap="round"
        strokeDasharray={`${2 * Math.PI * 48}`}
        strokeDashoffset={`${2 * Math.PI * 48 * (1 - normalizeReliability(rating) / 100)}`}
        className={`${getToneStrokeClass(tone)} transition-all duration-500`}
      />
    </svg>
    <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
      <div className={`text-3xl font-black leading-none tabular-nums ${getToneTextClass(tone)}`}>{formatReliability(rating)}</div>
      <div className={`mt-2 text-[10px] font-bold uppercase tracking-[0.1em] ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>
        Reliability
      </div>
    </div>
  </div>
);

const RoleSummaryCard = ({
  role,
  stats,
  isDarkMode,
  onReset,
}: {
  role: MeetingPolicyRole;
  stats: UserMeetingPolicyStats | null;
  isDarkMode: boolean;
  onReset: (role: MeetingPolicyRole) => void;
}) => {
  const reliabilityTone = getReliabilityTone(stats?.reliabilityRating);
  const RoleIcon = role === "LEARNER" ? FaUserGraduate : FaUserTie;
  const roleAccent =
    role === "LEARNER"
      ? "from-sky-500 via-emerald-500 to-teal-500"
      : "from-indigo-500 via-fuchsia-500 to-sky-500";
  const roleSurface =
    role === "LEARNER"
      ? isDarkMode
        ? "from-slate-950 via-sky-950/25 to-emerald-950/20"
        : "from-white via-sky-50/75 to-emerald-50/70"
      : isDarkMode
        ? "from-slate-950 via-indigo-950/25 to-fuchsia-950/20"
        : "from-white via-indigo-50/70 to-fuchsia-50/60";

  return (
    <section
      className={`relative overflow-hidden rounded-[2rem] border bg-gradient-to-br p-6 shadow-xl ${roleSurface} ${
        isDarkMode
          ? "border-slate-800/80"
          : "border-white/80"
      }`}
    >
      <div className={`absolute inset-x-0 top-0 h-1.5 bg-gradient-to-r ${roleAccent}`} />

      <div className="relative flex flex-col gap-5">
        <div className="flex justify-end">
          <button
            type="button"
            onClick={() => onReset(role)}
            className={`inline-flex items-center justify-center gap-2 rounded-2xl px-5 py-3 text-sm font-extrabold shadow-lg ring-1 transition-all hover:-translate-y-0.5 hover:shadow-xl ${
              isDarkMode
                ? "bg-gradient-to-r from-rose-950 to-amber-950 text-rose-100 ring-rose-800/70 hover:from-rose-900 hover:to-amber-900"
                : "bg-gradient-to-r from-rose-600 to-orange-500 text-white ring-rose-200 hover:from-rose-700 hover:to-orange-600"
            }`}
          >
            <FaUndo />
            Reset {formatLabel(role)} Policy
          </button>
        </div>

        <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
          <div className="min-w-0">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div className="flex items-center gap-4">
              <div
                className={`flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br ${roleAccent} text-white shadow-lg`}
              >
                <RoleIcon className="text-xl" />
              </div>
              <div>
                <div className={`text-xs font-bold uppercase tracking-[0.1em] ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>
                  {formatLabel(role)}
                </div>
                <h2 className={`mt-1 text-2xl font-extrabold leading-tight tracking-tight ${isDarkMode ? "text-white" : "text-slate-950"}`}>
                  Meeting Policy
                </h2>
              </div>
            </div>
          </div>

            <div className="mt-5 grid gap-3 sm:grid-cols-2">
              <CounterTile
                label="Penalty score"
                value={stats?.penaltyScore}
                tone={(stats?.penaltyScore || 0) > 0 ? "rose" : "emerald"}
                isDarkMode={isDarkMode}
              />
              <CounterTile
                label="Policy events"
                value={stats?.meetingPolicyEventsCount}
                tone="blue"
                isDarkMode={isDarkMode}
              />
            </div>
          </div>

          <div className={`rounded-[1.75rem] border p-4 shadow-lg ${getToneClasses(reliabilityTone, isDarkMode)}`}>
            <ReliabilityProgress rating={stats?.reliabilityRating} tone={reliabilityTone} isDarkMode={isDarkMode} />
          </div>
        </div>
      </div>

      {!stats ? (
        <div
          className={`relative mt-6 rounded-2xl border border-dashed p-5 text-sm ${
            isDarkMode ? "border-slate-700 text-slate-300" : "border-slate-200 text-slate-600"
          }`}
        >
          No {formatLabel(role).toLowerCase()} meeting policy stats document was found for this user.
        </div>
      ) : (
        <>
          <div className="relative mt-5">
            <RestrictionBadge restriction={stats.activeRestriction} isDarkMode={isDarkMode} />
          </div>

          <div className="relative mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <CounterTile label="Completed" value={stats.completedCount} tone="emerald" isDarkMode={isDarkMode} />
            <CounterTile label="Late arrivals" value={stats.lateArrivalCount} tone="amber" isDarkMode={isDarkMode} />
            <CounterTile label="No shows" value={stats.noShowCount} tone="rose" isDarkMode={isDarkMode} />
            <CounterTile label="Critical cancels" value={stats.criticalCancellationCount} tone="rose" isDarkMode={isDarkMode} />
            <CounterTile label="Late cancels" value={stats.lateCancellationCount} tone="amber" isDarkMode={isDarkMode} />
            <CounterTile label="Events" value={stats.meetingPolicyEventsCount} tone="blue" isDarkMode={isDarkMode} />
          </div>

          <div className={`relative mt-5 text-xs font-semibold ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>
            Last event: {formatDate(stats.lastEventAt)} / Updated: {formatDate(stats.updatedAt)}
          </div>
        </>
      )}
    </section>
  );
};

const ResetMeetingPolicyModal = ({
  role,
  isOpen,
  isSubmitting,
  onClose,
  onSubmit,
}: {
  role: MeetingPolicyRole | null;
  isOpen: boolean;
  isSubmitting: boolean;
  onClose: () => void;
  onSubmit: (payload: { role: MeetingPolicyRole; reason?: string; resetAll: boolean }) => Promise<void>;
}) => {
  const [reason, setReason] = React.useState("");
  const [resetAll, setResetAll] = React.useState(false);

  React.useEffect(() => {
    if (!isOpen) {
      setReason("");
      setResetAll(false);
    }
  }, [isOpen]);

  if (!role) return null;

  const handleSubmit = async () => {
    await onSubmit({
      role,
      reason: reason.trim() || undefined,
      resetAll,
    });
  };

  return (
    <Modal isOpen={isOpen} onClose={isSubmitting ? undefined : onClose} size={2} hideFooter>
      <div className="p-1">
        <div className="mb-6 flex items-start gap-4">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-amber-500 to-orange-600 text-white shadow-lg">
            <FaUndo />
          </div>
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.1em] text-amber-600">Admin reset</div>
            <h3 className="mt-1 text-2xl font-extrabold tracking-tight text-slate-950">
              Reset {formatLabel(role)} Policy
            </h3>
            <p className="mt-2 text-sm leading-6 text-slate-600">
              Normal reset clears the penalty score and active restriction only. Full reset also clears reliability
              counters and sets the rating back to 100.
            </p>
          </div>
        </div>

        <div className="space-y-5">
          <div className="rounded-2xl border border-sky-200 bg-sky-50 p-4 text-sm text-sky-900">
            <div className="font-bold">Role being reset</div>
            <div className="mt-1">{formatLabel(role)}</div>
          </div>

          <label className="block">
            <span className="mb-2 block text-sm font-bold text-slate-700">Reason</span>
            <textarea
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              rows={4}
              className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 shadow-sm outline-none transition focus:border-amber-400 focus:ring-4 focus:ring-amber-100"
              placeholder="Optional note for the ADMIN_RESET event"
              disabled={isSubmitting}
            />
          </label>

          <label
            className={`flex cursor-pointer items-start gap-3 rounded-2xl border p-4 ${
              resetAll ? "border-rose-200 bg-rose-50" : "border-slate-200 bg-slate-50"
            }`}
          >
            <input
              type="checkbox"
              checked={resetAll}
              onChange={(event) => setResetAll(event.target.checked)}
              disabled={isSubmitting}
              className="mt-1 h-4 w-4 rounded border-slate-300 text-rose-600 focus:ring-rose-500"
            />
            <span>
              <span className="block text-sm font-bold text-slate-900">Reset all reliability counters</span>
              <span className="mt-1 block text-sm leading-6 text-slate-600">
                Leave unchecked to clear only penalty score and active restriction. Check this for a full reliability
                reset.
              </span>
            </span>
          </label>
        </div>

        <div className="mt-6 flex flex-col-reverse gap-3 border-t border-slate-200 pt-5 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="rounded-xl bg-slate-100 px-5 py-2.5 text-sm font-bold text-slate-700 transition hover:bg-slate-200 disabled:cursor-not-allowed disabled:opacity-60"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="rounded-xl bg-gradient-to-r from-amber-500 to-orange-600 px-5 py-2.5 text-sm font-bold text-white shadow-lg transition hover:from-amber-600 hover:to-orange-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {isSubmitting ? "Resetting..." : resetAll ? "Full Reset Policy" : "Reset Policy"}
          </button>
        </div>
      </div>
    </Modal>
  );
};

const EventHistoryRow = ({ event, isDarkMode }: { event: MeetingPolicyEvent; isDarkMode: boolean }) => {
  const tone = getEventTone(event.eventType, event.pointsDelta);

  return (
    <tr className={`transition-colors ${isDarkMode ? "border-slate-800 hover:bg-slate-900/65" : "border-slate-100 hover:bg-slate-50/80"}`}>
      <td className="px-4 py-4 align-top">
        <div className={`font-semibold leading-snug tabular-nums ${isDarkMode ? "text-white" : "text-slate-900"}`}>
          {formatDate(event.occurredAt || event.createdAt)}
        </div>
        <div className={`mt-1 text-xs ${isDarkMode ? "text-slate-500" : "text-slate-400"}`}>Created {formatDate(event.createdAt)}</div>
      </td>
      <td className="px-4 py-4 align-top">
        <span className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-bold ${getToneClasses("blue", isDarkMode)}`}>
          {formatLabel(event.role)}
        </span>
      </td>
      <td className="px-4 py-4 align-top">
        <span className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-bold ${getToneClasses(tone, isDarkMode)}`}>
          {formatLabel(event.eventType)}
        </span>
      </td>
      <td className="px-4 py-4 align-top">
        <span
          className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-bold ${
            event.pointsDelta < 0
              ? isDarkMode
                ? "bg-rose-950 text-rose-200 ring-1 ring-rose-800"
                : "bg-rose-100 text-rose-800"
              : event.pointsDelta > 0
                ? isDarkMode
                  ? "bg-emerald-950 text-emerald-200 ring-1 ring-emerald-800"
                  : "bg-emerald-100 text-emerald-800"
                : isDarkMode
                  ? "bg-slate-900 text-slate-300 ring-1 ring-slate-800"
                  : "bg-slate-100 text-slate-700"
          }`}
        >
          {event.pointsDelta < 0 ? <FaMinusCircle /> : <FaInfoCircle />}
          {event.pointsDelta > 0 ? "+" : ""}
          {event.pointsDelta}
        </span>
      </td>
      <td className="px-4 py-4 align-top text-sm">{formatLabel(event.timingWindow)}</td>
      <td className="px-4 py-4 align-top">
        <div className="space-y-1 text-xs">
          <div className="flex items-center gap-1">
            <FaIdBadge className="text-cyan-500" />
            <span className="break-all font-mono text-[11px]">{event.bookingId || "No booking"}</span>
          </div>
          <div className="flex items-center gap-1">
            <FaClock className="text-indigo-500" />
            <span className="break-all font-mono text-[11px]">{event.sessionId || "No session"}</span>
          </div>
        </div>
      </td>
      <td className="px-4 py-4 align-top">
        {event.restrictionApplied ? (
          <RestrictionBadge restriction={event.restrictionApplied} isDarkMode={isDarkMode} />
        ) : (
          <span className="text-sm text-slate-400">None</span>
        )}
      </td>
      <td className="px-4 py-4 align-top">
        <MetadataSummary metadata={event.metadata} isDarkMode={isDarkMode} />
      </td>
    </tr>
  );
};

const MeetingPolicyPage = () => {
  const { userId } = useParams<{ userId: string }>();
  const { isDarkMode } = useTheme();
  const dispatch = useAppDispatch();
  const [selectedResetRole, setSelectedResetRole] = React.useState<MeetingPolicyRole | null>(null);
  const [resetMeetingPolicyStats, { isLoading: resetSubmitting }] = useResetMeetingPolicyStatsMutation();
  const { data: user, isLoading: userLoading, isError: userError } = useGetUserByIdQuery(userId || "", {
    skip: !userId,
  });
  const {
    data: statsByRole,
    isLoading: statsLoading,
    isFetching: statsFetching,
    isError: statsError,
    refetch: refetchStats,
  } = useGetUserMeetingPolicyStatsQuery(userId || "", {
    skip: !userId,
  });
  const {
    data: events = [],
    isLoading: eventsLoading,
    isFetching: eventsFetching,
    isError: eventsError,
    error: eventsLoadError,
    refetch: refetchEvents,
  } = useGetUserMeetingPolicyEventsQuery(userId || "", {
    skip: !userId,
  });

  if (!userId) {
    return <div className="p-6 text-red-600">User ID not found.</div>;
  }

  if (userLoading || statsLoading || eventsLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Spinner />
      </div>
    );
  }

  if (userError || !user) {
    return (
      <div className="mx-auto max-w-4xl px-4 py-10">
        <div className="rounded-3xl border border-red-200 bg-red-50 p-8 text-center">
          <h2 className="text-2xl font-bold text-red-800">User not found</h2>
          <p className="mt-3 text-red-600">We could not load this user details page.</p>
          <Link
            to="/users"
            className="mt-6 inline-flex rounded-2xl bg-red-600 px-5 py-3 font-semibold text-white no-underline transition-colors hover:bg-red-700 hover:no-underline"
          >
            Back to Users
          </Link>
        </div>
      </div>
    );
  }

  const visiblePolicyRoles = getPolicyRolesForUser(user.roles);
  const activeRestrictions = visiblePolicyRoles.filter((role) => statsByRole?.[role]?.activeRestriction).length;
  const totalEvents = visiblePolicyRoles.reduce(
    (total, role) => total + (statsByRole?.[role]?.meetingPolicyEventsCount || 0),
    0
  );
  const eventsErrorText = eventsError ? getQueryErrorText(eventsLoadError) : "";
  const eventsIndexLink = eventsErrorText ? getFirstLink(eventsErrorText) : undefined;
  const handleResetSubmit = async ({
    role,
    reason,
    resetAll,
  }: {
    role: MeetingPolicyRole;
    reason?: string;
    resetAll: boolean;
  }) => {
    try {
      await resetMeetingPolicyStats({
        userId,
        role,
        reason,
        resetAll,
      }).unwrap();

      await Promise.all([refetchStats(), refetchEvents()]);
      setSelectedResetRole(null);
      dispatch(
        showToast({
          type: "success",
          message: `${formatLabel(role)} meeting policy reset successfully.`,
        })
      );
    } catch (error) {
      dispatch(
        showToast({
          type: "error",
          message: getQueryErrorText(error) || "Failed to reset meeting policy.",
        })
      );
    }
  };

  return (
    <div
      className={`min-h-screen py-8 font-sans antialiased ${
        isDarkMode
          ? "bg-gradient-to-br from-gray-950 via-slate-950 to-gray-900"
          : "bg-gradient-to-br from-slate-50 via-white to-sky-50"
      }`}
    >
      <div className="mx-auto max-w-7xl px-4">
        <Link
          to={`/users/${userId}`}
          className={`mb-6 inline-flex items-center gap-3 no-underline transition-colors hover:no-underline ${
            isDarkMode ? "text-emerald-300 hover:text-emerald-200" : "text-emerald-600 hover:text-emerald-700"
          }`}
        >
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 text-white shadow-lg">
            <FaArrowLeft />
          </div>
          <span className="text-base font-semibold tracking-tight">Back to User Details</span>
        </Link>

        <div
          className={`relative overflow-hidden rounded-[2rem] border p-8 shadow-2xl backdrop-blur ${
            isDarkMode
              ? "border-slate-800/80 bg-slate-950/80"
              : "border-white/80 bg-white/95"
          }`}
        >
          <div className="absolute inset-x-0 top-0 h-1.5 bg-gradient-to-r from-sky-500 via-emerald-500 to-amber-400" />
          <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
            <div className="relative">
              <div className="mb-4 inline-flex h-16 w-16 items-center justify-center rounded-3xl bg-gradient-to-br from-sky-500 via-emerald-500 to-teal-600 text-white shadow-xl">
                <FaBalanceScale className="text-2xl" />
              </div>
              <h1 className={`text-4xl font-black leading-tight tracking-tight sm:text-5xl ${isDarkMode ? "text-white" : "text-slate-950"}`}>
                Meeting Policy
              </h1>
              <p className={`mt-3 max-w-3xl text-base font-normal leading-7 sm:text-lg ${isDarkMode ? "text-slate-300" : "text-slate-600"}`}>
                Role-specific reliability, penalties, restrictions, and append-only policy history for{" "}
                <span className={`font-extrabold ${isDarkMode ? "text-white" : "text-slate-950"}`}>{user.name}</span>.
              </p>
            </div>
            <div className="relative grid gap-3 sm:grid-cols-2">
              <CounterTile
                label="Active restrictions"
                value={activeRestrictions}
                tone={activeRestrictions > 0 ? "rose" : "emerald"}
                isDarkMode={isDarkMode}
              />
              <CounterTile label="Recorded events" value={totalEvents || events.length} tone="blue" isDarkMode={isDarkMode} />
            </div>
          </div>

          {(statsError || eventsError) && (
            <div className={`mt-6 rounded-2xl border px-4 py-3 text-sm ${getToneClasses("amber", isDarkMode)}`}>
              Some meeting policy data could not be loaded. The sections below show whatever was available.
            </div>
          )}

          <div className={`mt-8 grid gap-6 ${visiblePolicyRoles.length > 1 ? "xl:grid-cols-2" : "grid-cols-1"}`}>
            {visiblePolicyRoles.map((role) => (
              <RoleSummaryCard
                key={role}
                role={role}
                stats={statsByRole?.[role] || null}
                isDarkMode={isDarkMode}
                onReset={setSelectedResetRole}
              />
            ))}
          </div>

          <section
            className={`mt-8 rounded-[2rem] border p-6 shadow-xl ${
              isDarkMode ? "border-slate-800/80 bg-slate-950/80" : "border-white/80 bg-white/95"
            }`}
          >
            <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-4">
                <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-sky-500 to-indigo-600 text-white shadow-lg">
                  <FaHistory />
                </div>
                <div>
                  <h2 className={`text-2xl font-extrabold leading-tight tracking-tight ${isDarkMode ? "text-white" : "text-slate-950"}`}>Event History</h2>
                  <p className={`text-sm font-normal leading-6 ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>
                    Newest policy events first. Late arrivals and admin resets may carry zero points.
                  </p>
                </div>
              </div>
              <div className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-bold tracking-tight ${getToneClasses("blue", isDarkMode)}`}>
                <FaCalendarAlt />
                Showing up to 75
              </div>
            </div>

            {eventsError && (
              <div
                className={`mb-6 rounded-2xl border p-4 text-sm ${
                  isDarkMode
                    ? "border-rose-800/70 bg-rose-950/30 text-rose-100"
                    : "border-rose-200 bg-rose-50 text-rose-900"
                }`}
              >
                <h3 className="mb-2 text-base font-bold">Error Loading Meeting Policy Events</h3>
                <pre className="whitespace-pre-wrap break-words text-xs leading-5">{eventsErrorText}</pre>
                {eventsIndexLink && (
                  <a
                    href={eventsIndexLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-4 inline-flex rounded-xl bg-sky-600 px-4 py-2 text-sm font-bold text-white no-underline shadow-sm transition-colors hover:bg-sky-700 hover:no-underline"
                  >
                    Open Firestore index creation link
                  </a>
                )}
              </div>
            )}

            {events.length === 0 ? (
              <div
                className={`rounded-3xl border border-dashed p-10 text-center ${
                  isDarkMode ? "border-cyan-800/70 bg-cyan-950/30 text-cyan-50" : "border-cyan-200 bg-cyan-50/80 text-slate-900"
                }`}
              >
                <div className={`mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl ${isDarkMode ? "bg-slate-900" : "bg-white"}`}>
                  <FaUndo className={isDarkMode ? "text-cyan-300" : "text-cyan-500"} />
                </div>
                <h3 className="text-xl font-semibold">No meeting policy events yet</h3>
                <p className={`mt-2 text-sm ${isDarkMode ? "text-slate-300" : "text-slate-600"}`}>
                  No entries were found in meeting_policy_events for this user.
                </p>
              </div>
            ) : (
              <div className={`overflow-x-auto rounded-3xl border shadow-sm ${isDarkMode ? "border-slate-800" : "border-slate-200"}`}>
                <table className={`min-w-full divide-y text-left text-sm leading-6 ${isDarkMode ? "divide-slate-800 text-slate-300" : "divide-slate-100 text-slate-700"}`}>
                  <thead className={isDarkMode ? "bg-slate-900/90 text-slate-300" : "bg-gradient-to-r from-slate-50 via-sky-50 to-emerald-50 text-slate-700"}>
                    <tr>
                      <th className="px-4 py-3 text-[11px] font-extrabold uppercase tracking-[0.1em]">Date</th>
                      <th className="px-4 py-3 text-[11px] font-extrabold uppercase tracking-[0.1em]">Role</th>
                      <th className="px-4 py-3 text-[11px] font-extrabold uppercase tracking-[0.1em]">Type</th>
                      <th className="px-4 py-3 text-[11px] font-extrabold uppercase tracking-[0.1em]">Points</th>
                      <th className="px-4 py-3 text-[11px] font-extrabold uppercase tracking-[0.1em]">Timing</th>
                      <th className="px-4 py-3 text-[11px] font-extrabold uppercase tracking-[0.1em]">Booking / Session</th>
                      <th className="px-4 py-3 text-[11px] font-extrabold uppercase tracking-[0.1em]">Restriction</th>
                      <th className="px-4 py-3 text-[11px] font-extrabold uppercase tracking-[0.1em]">Metadata</th>
                    </tr>
                  </thead>
                  <tbody className={`divide-y ${isDarkMode ? "divide-slate-800" : "divide-slate-100"}`}>
                    {events.map((event) => (
                      <EventHistoryRow key={event.id} event={event} isDarkMode={isDarkMode} />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          {(statsFetching || eventsFetching) && (
            <div className={`mt-6 text-sm font-medium ${isDarkMode ? "text-slate-400" : "text-gray-500"}`}>
              Refreshing meeting policy data...
            </div>
          )}
        </div>
      </div>

      <ResetMeetingPolicyModal
        role={selectedResetRole}
        isOpen={!!selectedResetRole}
        isSubmitting={resetSubmitting}
        onClose={() => setSelectedResetRole(null)}
        onSubmit={handleResetSubmit}
      />
    </div>
  );
};

export default MeetingPolicyPage;
